import { useState, forwardRef, useImperativeHandle, useCallback } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  CreateGameNetworkExclusionRequest,
  Platform,
} from "views/admin/gameNetworkExclusions/types";
import {
  getPartnersData,
  getPlatforms,
  createGameNetworkExclusion,
} from "views/admin/gameNetworkExclusions/services";
import { Partner } from "redux/partners/reducer";
import { useTableContext } from "contexts/TableContext";
import { SearchableSelect } from "components/filters/SearchableSelect";

interface FormData {
  partnerId: number | null;
  platformId: number;
  packageName: string;
  network: string;
}

interface AddExclusionModalProps {}

export interface AddExclusionModalRef {
  openDialog: () => void;
  closeDialog: () => void;
}

const AddExclusionModal = forwardRef<
  AddExclusionModalRef,
  AddExclusionModalProps
>((props, ref) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { refreshData } = useTableContext();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      partnerId: null,
      platformId: 0,
      packageName: "",
      network: "",
    },
    mode: "onChange",
  });

  const loadFormData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const [partnersData, platformsData] = await Promise.all([
        getPartnersData(),
        getPlatforms(),
      ]);

      setPartners(partnersData.data);
      setPlatforms(platformsData);
    } catch (error) {
      console.error("Error loading form data:", error);
      toast({
        title: "Error",
        description: "Failed to load form data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingData(false);
    }
  }, [toast]);

  const openDialog = useCallback(() => {
    if (partners.length === 0) {
      loadFormData();
    }
    onOpen();
  }, [onOpen, partners, loadFormData]);

  const closeDialog = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  useImperativeHandle(ref, () => ({
    openDialog,
    closeDialog,
  }));

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        setIsSubmitting(true);

        const exclusionData: CreateGameNetworkExclusionRequest = {
          partnerId: data.partnerId || null,
          platformId: Number(data.platformId),
          packageName: data.packageName || null,
          network: data.network,
        };

        await createGameNetworkExclusion(exclusionData);

        toast({
          title: "Success",
          description: "Game network exclusion created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        closeDialog();
        refreshData();
      } catch (error: any) {
        console.error("Error creating exclusion:", error);
        toast({
          title: "Error",
          description:
            error.message || "Failed to create game network exclusion",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [toast, refreshData, closeDialog]
  );

  return (
    <Modal isOpen={isOpen} onClose={closeDialog} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Game Network Exclusion</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Partner (Optional)</FormLabel>
              <Controller
                name="partnerId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Search and select a partner (optional)"
                    isDisabled={false}
                    options={partners}
                    valueKey="partnerId"
                    searchKeys={[
                      "companyName",
                      "firstName",
                      "lastName",
                      "mail",
                    ]}
                    getDisplayText={(partner) => {
                      const name = [partner.firstName, partner.lastName]
                        .filter(Boolean)
                        .join(" ");
                      const company = partner.companyName || "";

                      if (name && company) {
                        return `${name} (${company})`;
                      }
                      return company || name || "Unknown";
                    }}
                    getSecondaryText={(partner) => partner.mail || null}
                    noOptionsText="No partners found"
                  />
                )}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.platformId} mb={4}>
              <FormLabel>Platform</FormLabel>
              <Controller
                name="platformId"
                control={control}
                rules={{
                  required: "Please select a platform",
                  validate: (value) => value > 0 || "Please select a platform",
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select a platform"
                    isDisabled={isLoadingData}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  >
                    {platforms.map((platform) => (
                      <option key={platform.id} value={platform.id}>
                        {platform.name}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.platformId && errors.platformId.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Package Name (Optional)</FormLabel>
              <Input
                {...register("packageName")}
                placeholder="e.g., com.example.app"
              />
            </FormControl>

            <FormControl isInvalid={!!errors.network}>
              <FormLabel>Network</FormLabel>
              <Input
                {...register("network", {
                  required: "Network name is required",
                  minLength: {
                    value: 2,
                    message: "Network name must be at least 2 characters",
                  },
                  maxLength: {
                    value: 100,
                    message: "Network name must not exceed 100 characters",
                  },
                })}
                placeholder="Enter network name (e.g., Hang My Ads)"
              />
              <FormErrorMessage>
                {errors.network && errors.network.message}
              </FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={closeDialog}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting || isLoadingData}
              loadingText={isLoadingData ? "Loading..." : "Creating..."}
              isDisabled={!isValid}
            >
              Create Exclusion
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
});

export default AddExclusionModal;
